import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/api/client";
import { flowDagOverlayQueryKey } from "@/api/dag";
import { PROJECTS_ROOT_QUERY_KEY } from "@/api/projects";
import { RUNS_QUERY_KEY } from "@/api/runs";

// Wire-format mirrors the flow routes in
// src/flowstack/server/routes/flows.py (P1.2a + EPIC-001 U5). Used by
// the Flow picker, the Flow manifest display, and Flow submission.

export interface FlowSummary {
  name: string;
  version: string;
  display_name: string;
  description: string;
  node_count: number;
}

export interface FlowListResponse {
  flows: FlowSummary[];
}

/** Full FlowSchema payload — round-trips ``FlowSchema.model_dump``
 * so the GUI's form generator sees every declared field (inputs,
 * outputs, parameters, nodes) and renders the right controls. */
export interface FlowManifestResponse {
  manifest: Record<string, unknown>;
}

export const FLOWS_QUERY_KEY = ["flows", "list"] as const;

export function flowManifestQueryKey(name: string): readonly unknown[] {
  return ["flows", "manifest", name] as const;
}

export function useFlows() {
  return useQuery({
    queryKey: FLOWS_QUERY_KEY,
    queryFn: async (): Promise<FlowSummary[]> => {
      const response = await apiFetch<FlowListResponse>("/api/flows");
      return response.flows;
    },
    staleTime: 5 * 60_000,
  });
}

export function useFlowManifest(name: string) {
  return useQuery({
    queryKey: flowManifestQueryKey(name),
    queryFn: async (): Promise<FlowManifestResponse> =>
      apiFetch<FlowManifestResponse>(`/api/flows/${encodeURIComponent(name)}/manifest`),
    staleTime: 5 * 60_000,
    enabled: Boolean(name),
  });
}

// ---- Submission (EPIC-001 U5) ---------------------------------------

export interface SubmitFlowRequest {
  flow_name: string;
  // Declared FLOW-level input name → artifact content_hash. Seeding the
  // first artifact is the SDK-seed path (client.types.Molecule.from_sdf);
  // the GUI picks an already-catalogued artifact, same as the Tile page.
  input_artifacts: Record<string, string>;
  parameters?: Record<string, unknown>;
  override_failed_upstream?: boolean;
}

export interface SubmitFlowResponse {
  flow_name: string;
  flow_version: string;
  node_count: number;
  // The durable ``flow_runs`` row this submission created (#371 D0). The
  // route already returns it; the portal shows it since fs#1797. Optional
  // on the wire: an older API that predates the field must not print
  // "undefined" in the submitted notice.
  flow_run_id?: string;
  // Always "validating": dispatch is async. The flow's per-node children
  // appear (and terminalize) afterwards — watch the DAG overlay or /runs.
  state: string;
  failed_input_artifacts_overridden: string[];
}

/** What ``POST /api/flows/dry-run`` returns (flowstack#1398).
 *
 * The same body as a submission, run through the same
 * ``_resolve_and_compile`` — with nothing written and nothing dispatched.
 * Deliberately carries no ``state`` and no flow-run id: there is no run.
 */
export interface DryRunFlowResponse {
  flow_name: string;
  flow_version: string;
  node_count: number;
  /** How many of ``node_count`` expand per-child at dispatch, which is why
   *  the figure is a floor rather than a total. Fan-out WIDTH is a property
   *  of the molecule and is not knowable until the run produces it. */
  fanout_node_count: number;
  /** Node name → the ``when:`` predicate that excluded it from this compile. */
  gated_nodes: Record<string, string>;
  failed_input_artifacts_overridden: string[];
}

/**
 * Compile a Flow submission without dispatching it (flowstack#1398).
 *
 * The canvas's **Dry run**, beside **Submit flow**. It answers the two
 * questions worth asking before 15+ billed runs — are the inputs wired, and
 * does this template compile against the tiles deployed right now — and it
 * answers them from the *same* server code path the submission takes, so a
 * green dry run is a statement about the submission rather than about a
 * plausible neighbour of it.
 *
 * No cache invalidation, because nothing changed. That is the whole point.
 */
export function useDryRunFlow() {
  return useMutation({
    mutationFn: async (body: SubmitFlowRequest): Promise<DryRunFlowResponse> =>
      apiFetch<DryRunFlowResponse>("/api/flows/dry-run", {
        method: "POST",
        json: body,
      }),
  });
}

/**
 * Submit a Flow. The backend compiles the template (4xx if the inputs
 * don't satisfy it) and dispatches the DAG in the background, persisting
 * one run per fan-out child.
 *
 * There is no flow-run id to navigate to — FlowStack has no flow-run
 * table. The flow's identity on the read side is its NAME: the caller
 * lands on the DAG overlay, which projects each node's live run state.
 */
export function useSubmitFlow() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SubmitFlowRequest): Promise<SubmitFlowResponse> =>
      apiFetch<SubmitFlowResponse>("/api/flows/submit", {
        method: "POST",
        json: body,
      }),
    onSuccess: (data) => {
      // The children land asynchronously, so drop the caches the flow
      // views read rather than waiting out their stale windows.
      queryClient.invalidateQueries({ queryKey: RUNS_QUERY_KEY });
      // The whole `projects` prefix — list, detail AND the project-scoped run
      // feed the submitted notice links back to (fs#1797). The list key alone
      // left that feed serving its stale window after the user followed it.
      queryClient.invalidateQueries({ queryKey: PROJECTS_ROOT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: flowDagOverlayQueryKey(data.flow_name) });
    },
  });
}
